/**
 * Shown when a brief cannot be built because neither source exists: Lever no longer knows the
 * posting the hiring-plan row names, and there is no readable kick-off sheet. A toast vanished
 * before anyone could act on it; this stays, says which source is missing, and links the row in
 * the hiring database where both are fixed (Sofia, Oct 15 2026).
 */
import type { BriefSourceProblem } from '../../services/taOutreachApi';

const KICKOFF_LINE: Record<BriefSourceProblem['kickoff'], string> = {
  missing: 'There is no kick-off sheet for this role.',
  not_connected: 'A kick-off sheet is linked, but Google is not connected, so it cannot be read.',
  unreadable: 'A kick-off sheet is linked, but it could not be read with your Google access.',
  unknown: 'No kick-off sheet could be read for this role.',
};

const link = 'font-semibold text-[#0072EA] underline';

export function BriefSourceProblemBox({
  problem,
  connecting,
  onConnectGmail,
}: {
  problem: BriefSourceProblem;
  connecting?: boolean;
  /** Connects Google and builds the brief again. */
  onConnectGmail?: () => void;
}) {
  return (
    <div role="alert" data-testid="brief-source-problem" className="space-y-1.5 rounded-md border border-solid border-[#E71D3F] bg-[#FCDFE4] px-4 py-3 text-[13px] text-[#1A1A1A]">
      <b>The brief cannot be built yet.</b>
      <ul className="list-disc space-y-0.5 pl-5">
        <li>Lever does not know the posting this role points at.</li>
        <li>{KICKOFF_LINE[problem.kickoff]}</li>
      </ul>
      <p className="flex flex-wrap gap-x-4 gap-y-1 pt-0.5">
        {problem.airtableUrl ? (
          <a href={problem.airtableUrl} target="_blank" rel="noreferrer" className={link}>
            Open the role in the hiring database ↗
          </a>
        ) : (
          <span className="text-[#707070]">The role’s row in the hiring database could not be found.</span>
        )}
        {problem.kickoffUrl && (
          <a href={problem.kickoffUrl} target="_blank" rel="noreferrer" className={link}>
            Open the kick-off sheet ↗
          </a>
        )}
        {(problem.kickoff === 'not_connected' || problem.kickoff === 'unreadable') && onConnectGmail && (
          <button type="button" className={link} disabled={connecting} onClick={onConnectGmail}>
            {connecting ? 'Connecting…' : problem.kickoff === 'unreadable' ? 'Reconnect Google and try again' : 'Connect Google and try again'}
          </button>
        )}
      </p>
      <p className="text-xs text-[#707070]">There: fix the Lever Job Posting ID, or add the kick-off sheet. Then build the brief again.</p>
    </div>
  );
}
